import { useState } from "react";
import VerseRow from "./verserow";
import { Plus } from "lucide-react";
import ChristmasImage from "../../assets/images/christmas.jpg";

const isValidYouTubeUrl = (url) => {
  const regex =
    /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/)|youtu\.be\/)[\w-]{11}(\?.*)?$/;
  return regex.test(url);
};

const DevotionCard = ({
  plan,
  journals = [],
  testimonials = [],
  onAddVerse,
  onChange,
  // NEW: Add props for selected items
  selectedJournals = [],
  selectedTestimonials = [],
  onJournalsChange = () => {},
  onTestimonialsChange = () => {},
}) => {
  const [showJournalModal, setShowJournalModal] = useState(false);
  const [showTestimonialModal, setShowTestimonialModal] = useState(false);
  const [videoError, setVideoError] = useState("");

  const handleVideoChange = (e) => {
    const value = e.target.value;

    if (value && !isValidYouTubeUrl(value)) {
      setVideoError("Please enter a valid YouTube URL");
    } else {
      setVideoError("");
    }

    onChange({
      ...plan,
      youtubeUrl: value,
    });
  };

  const handleSelectJournal = (journal) => {
    // Check if already selected
    const exists = selectedJournals.find((j) => j._id === journal._id);
    if (!exists) {
      const updatedJournals = [...selectedJournals, journal];
      onJournalsChange(updatedJournals);
    }
    setShowJournalModal(false);
  };

  const handleSelectTestimonial = (testimonial) => {
    const exists = selectedTestimonials.find((t) => t._id === testimonial._id);
    if (!exists) {
      const updatedTestimonials = [...selectedTestimonials, testimonial];
      onTestimonialsChange(updatedTestimonials);
    }
    setShowTestimonialModal(false);
  };

  // Remove journal
  const removeJournal = (id) => {
    const updatedJournals = selectedJournals.filter((j) => j._id !== id);
    onJournalsChange(updatedJournals);
  };

  // Remove testimony
  const removeTestimony = (id) => {
    const updatedTestimonials = selectedTestimonials.filter((t) => t._id !== id);
    onTestimonialsChange(updatedTestimonials);
  };

  return (
    <>
      <div className="rounded-xl p-4 bg-[#1f1f1f]">
        {/* Description */}
        <textarea
          placeholder="What is God asking you to share?"
          value={plan.description}
          onChange={(e) => onChange({ ...plan, description: e.target.value })}
          className="bg-[#2a2a2a] text-[#A3A3A3] rounded-md p-3 text-sm mb-4 w-full resize-none h-[90px] focus:outline-none"
        />

        {/* ADD TESTIMONIES */}
        <div
          className="flex items-center gap-2 text-[#A3A3A3] cursor-pointer mb-3"
          onClick={() => setShowTestimonialModal(true)}
        >
          <Plus className="w-4 h-4 border rounded-full border-[#a3a3a3]" />
          Add Testimonies
        </div>

        {/* SELECTED TESTIMONIES */}
        {selectedTestimonials.length > 0 && (
          <div className="flex gap-3 mb-4 overflow-x-auto">
            {selectedTestimonials.map((testimonial) => (
              <div key={testimonial._id} className="min-w-[80px] max-w-[80px] relative">
                <button
                  onClick={() => removeTestimony(testimonial._id)}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs z-10"
                >
                  ×
                </button>
                <img
                  src={testimonial?.testomonial_rec_tumbnailUrl ?? ChristmasImage}
                  alt={testimonial.testimonial_name}
                  className="w-full h-[80px] object-cover rounded-lg"
                />
                <p className="text-xs text-center mt-1 truncate text-white">
                  {testimonial.testimonial_name}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* ADD JOURNALS */}
        <div
          className="flex items-center gap-2 text-[#A3A3A3] cursor-pointer mb-3"
          onClick={() => setShowJournalModal(true)}
        >
          <Plus className="w-4 h-4 border rounded-full border-[#a3a3a3]" />
          Add Journals
        </div>

        {/* SELECTED JOURNALS */}
        {selectedJournals.length > 0 && (
          <div className="flex gap-3 mb-4 overflow-x-auto">
            {selectedJournals.map((journal) => (
              <div key={journal._id} className="min-w-[80px] max-w-[80px] relative">
                <button
                  onClick={() => removeJournal(journal._id)}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs z-10"
                >
                  ×
                </button>
                <img
                  src={journal?.banner_Images?.[0]?.url_link ?? ChristmasImage}
                  alt={journal.journal_title}
                  className="w-full h-[80px] object-cover rounded-lg"
                />
                <p className="text-xs text-center mt-1 truncate text-white">
                  {journal.journal_title}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* VERSES */}
        <div className="space-y-3">
          {plan.verses.map((verse, idx) => (
            <VerseRow key={idx} verse={verse} />
          ))}

          <div
            className="flex items-center gap-2 text-[#A3A3A3] cursor-pointer"
            onClick={onAddVerse}
          >
            <Plus className="w-4 h-4 border rounded-full border-[#a3a3a3]" />
            Add Verse
          </div>
        </div>

        {/* YOUTUBE VIDEO */}
        <div className="flex flex-col gap-1 mt-4">
          <label className="text-[#A3A3A3] text-sm">
            YouTube Video Link
          </label>
          <input
            type="text"
            placeholder="https://www.youtube.com/watch?v=xxxx"
            value={plan.youtubeUrl || ""}
            onChange={handleVideoChange}
            className={`bg-[#2a2a2a] text-[#A3A3A3] rounded-md p-2 text-sm focus:outline-none
              ${videoError ? "border border-red-500" : "border border-transparent"}
            `}
          />
          {videoError && <p className="text-xs text-red-500">{videoError}</p>}
        </div>
      </div>

      {/* JOURNAL MODAL */}
      {showJournalModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
          <div className="bg-[#1a1a1a] w-[500px] max-h-[400px] rounded-xl p-4 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-semibold">Select Journal</h3>
              <button
                onClick={() => setShowJournalModal(false)}
                className="text-white"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {journals.map((journal) => (
                <div
                  key={journal._id}
                  onClick={() => handleSelectJournal(journal)}
                  className="cursor-pointer"
                >
                  <img
                    src={journal?.banner_Images?.[0]?.url_link ?? ChristmasImage}
                    alt={journal.journal_title}
                    className="w-full h-[100px] object-cover rounded-lg"
                  />
                  <p className="text-xs text-center mt-1 text-white truncate">
                    {journal.journal_title}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TESTIMONY MODAL */}
      {showTestimonialModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
          <div className="bg-[#1a1a1a] w-[500px] max-h-[400px] rounded-xl p-4 overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-semibold">Select Testimony</h3>
              <button
                onClick={() => setShowTestimonialModal(false)}
                className="text-white"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial._id}
                  onClick={() => handleSelectTestimonial(testimonial)}
                  className="cursor-pointer"
                >
                  <img
                    src={testimonial?.testomonial_rec_tumbnailUrl ?? ChristmasImage}
                    alt={testimonial.testimonial_name}
                    className="w-full h-[100px] object-cover rounded-lg"
                  />
                  <p className="text-xs text-center mt-1 text-white truncate">
                    {testimonial.testimonial_name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DevotionCard;